import { ScrollView, Alert, TextInput, Pressable } from "react-native";
import { useState } from "react";
import { useRouter } from "expo-router";
import { useUnistyles } from "react-native-unistyles";
import { View, Text, Button } from "@src/components";
import { Icon } from "@src/components/Icon";
import { sizes } from "@src/constants/sizes";
import { useEnvironment, setPendingEnvironmentSwitch } from "@src/config/environment-store";
import { useTenant } from "@src/features/tenants";
import {
  ENVIRONMENT_DEFINITIONS,
  resolveEnvironment,
  type EnvironmentEntry,
  type EnvironmentKey,
} from "@visioni/a3s-environments";

type BaseEnvironmentOption = {
  key: EnvironmentKey;
  label: string;
};

type StaticEnvironmentOption = BaseEnvironmentOption & {
  key: "prd" | "int";
};

type ParameterizedEnvironmentOption = BaseEnvironmentOption & {
  key: "mr" | "dev";
  inputPlaceholder: string;
  params: (input: string) => Record<string, string>;
};

type EnvironmentOption = StaticEnvironmentOption | ParameterizedEnvironmentOption;

const isParameterized = (opt: EnvironmentOption): opt is ParameterizedEnvironmentOption => "params" in opt;

const ENVIRONMENT_OPTIONS: EnvironmentOption[] = [
  { key: "prd", label: "Production" },
  { key: "int", label: "Integration" },
  {
    key: "mr",
    label: "Merge Request",
    inputPlaceholder: "MR ID (e.g., 1234)",
    params: (input) => ({ mergeRequestId: input }),
  },
  {
    key: "dev",
    label: "Development",
    inputPlaceholder: "AWS Account ID (e.g., 123456789012)",
    params: (input) => ({ account: input }),
  },
];

export default function EnvironmentScreen() {
  const { theme } = useUnistyles();
  const router = useRouter();
  const currentEnv = useEnvironment();
  const { selectedTenant } = useTenant();

  const [expandedOption, setExpandedOption] = useState<ParameterizedEnvironmentOption | null>(null);
  const [inputValue, setInputValue] = useState("");

  const selectedTenantRealm = selectedTenant?.realm.fqdn ?? "";
  const selectedTenantBaseUrl = selectedTenant?.realm.api ?? "";

  const confirmSwitch = (entry: EnvironmentEntry) => {
    Alert.alert("Switch Environment", `Switch to ${entry.fqdn}?\n\nThis will log you out and clear all cached data.`, [
      { text: "Cancel", style: "cancel" },
      {
        text: "Switch",
        style: "destructive",
        onPress: () => {
          setPendingEnvironmentSwitch(entry);
          router.replace("/auth/login");
        },
      },
    ]);
  };

  const handleOptionPress = (option: EnvironmentOption) => {
    if (isParameterized(option)) {
      const isExpanded = expandedOption?.key === option.key;
      setExpandedOption(isExpanded ? null : option);
      setInputValue("");
    } else if (currentEnv.key !== option.key) {
      confirmSwitch(resolveEnvironment(option.key));
    }
  };

  const handleSubmit = () => {
    if (expandedOption && inputValue.trim()) {
      confirmSwitch(resolveEnvironment(expandedOption.key, expandedOption.params(inputValue.trim()) as never));
    }
  };

  return (
    <View flex={1} bg="background">
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ padding: theme.spacing["3xl"] }}
        keyboardShouldPersistTaps="handled"
      >
        <View mb="3xl" p="3xl" bg="surface" borderWidth={1} borderColor="border" rounded={sizes.radius}>
          <Text textStyle="sm/regular" color="textMuted">
            Current Environment
          </Text>
          <Text textStyle="sm/semibold">{currentEnv.fqdn}</Text>
        </View>
        <View bg="surface" borderWidth={1} borderColor="border" rounded={sizes.radius} style={{ overflow: "hidden" }}>
          {ENVIRONMENT_OPTIONS.map((option, index) => {
            const isSelected = currentEnv.key === option.key;
            const isExpanded = expandedOption?.key === option.key;
            const isLast = index === ENVIRONMENT_OPTIONS.length - 1;

            return (
              <View key={option.key}>
                <Pressable
                  onPress={() => handleOptionPress(option)}
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    padding: theme.spacing["3xl"],
                    borderBottomWidth: isLast && !isExpanded ? 0 : 1,
                    borderBottomColor: theme.colors.border,
                    backgroundColor: isSelected ? theme.colors.background : undefined,
                  }}
                >
                  <View
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 11,
                      borderWidth: 2,
                      borderColor:
                        isSelected ? theme.colors.interactive.buttons.default_background : theme.colors.border,
                      marginRight: theme.spacing.lg,
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {isSelected && (
                      <View
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: 6,
                          backgroundColor: theme.colors.interactive.buttons.default_background,
                        }}
                      />
                    )}
                  </View>

                  <View style={{ flex: 1 }}>
                    <Text textStyle="sm/semibold">{option.label}</Text>
                    <Text textStyle="sm/regular" color="textMuted">
                      {ENVIRONMENT_DEFINITIONS[option.key].fqdn}
                    </Text>
                  </View>

                  {isParameterized(option) && (
                    <Icon name={isExpanded ? "chevronUp" : "chevronDown"} size={20} color={theme.colors.textMuted} />
                  )}
                </Pressable>

                {isExpanded && expandedOption && (
                  <View
                    style={{
                      padding: theme.spacing["3xl"],
                      borderBottomWidth: isLast ? 0 : 1,
                      borderBottomColor: theme.colors.border,
                      gap: theme.spacing.lg,
                    }}
                  >
                    <TextInput
                      style={{
                        borderWidth: 1,
                        borderColor: theme.colors.border,
                        borderRadius: sizes.radius,
                        padding: theme.spacing.lg,
                        color: theme.colors.text,
                        backgroundColor: theme.colors.background,
                      }}
                      placeholder={expandedOption.inputPlaceholder}
                      placeholderTextColor={theme.colors.textMuted}
                      value={inputValue}
                      onChangeText={setInputValue}
                      autoCapitalize="none"
                      autoCorrect={false}
                      autoFocus
                      onSubmitEditing={handleSubmit}
                      returnKeyType="go"
                    />
                    <Button
                      label="Switch"
                      variant="primary"
                      size="sm"
                      onPress={handleSubmit}
                      disabled={!inputValue.trim()}
                    />
                  </View>
                )}
              </View>
            );
          })}
        </View>

        <View mt="3xl" p="3xl" bg="surface" borderWidth={1} borderColor="border" rounded={sizes.radius} gap="sm">
          <Text textStyle="sm/regular" color="textMuted">
            Selected Organisation Realm
          </Text>
          <Text textStyle="sm/semibold">{selectedTenantRealm || "No organisation selected"}</Text>
          <Text textStyle="sm/regular" color="textMuted" mt="sm">
            Base URL
          </Text>
          <Text textStyle="sm/semibold">{selectedTenantBaseUrl || "No base URL available"}</Text>
        </View>
      </ScrollView>
    </View>
  );
}
